import { useQuery } from '@tanstack/react-query';
import { adminApi, type AiStatus } from '../api/admin';
import { useAuth } from '../hooks/useAuth';

// Admin-only sticky banner that appears while the Anthropic API behind the
// spell-check and SEO features is failing (most commonly: out of credits) and
// disappears on its own once it recovers. State comes from GET /api/admin/
// ai-status (server/src/services/aiHealth.ts), which the server caches, so this
// poll is cheap regardless of how many admins are connected.

function messageFor(status: AiStatus): { headline: string; detail: string } {
  switch (status.reason) {
    case 'credit_balance':
      return {
        headline: 'We need more credits for the API.',
        detail:
          'The Anthropic API is out of credits — spell-check and SEO generation are falling back to placeholders. Add credits at console.anthropic.com → Plans & Billing.',
      };
    case 'no_key':
      return {
        headline: 'The AI API key is not configured.',
        detail: 'Spell-check and SEO generation are disabled until ANTHROPIC_API_KEY is set.',
      };
    case 'auth':
      return {
        headline: 'The AI API key is invalid.',
        detail:
          'Anthropic rejected the API key — spell-check and SEO generation are failing. Check ANTHROPIC_API_KEY.',
      };
    default:
      return {
        headline: 'The AI API is currently unavailable.',
        detail:
          'Spell-check and SEO generation are falling back to placeholders. ' +
          (status.detail || 'Retrying automatically.'),
      };
  }
}

export default function ApiCreditBanner() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const { data } = useQuery({
    queryKey: ['admin', 'ai-status'],
    queryFn: adminApi.getAiStatus,
    enabled: isAdmin,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
    retry: false,
  });

  if (!isAdmin || !data || data.ok) return null;

  const { headline, detail } = messageFor(data);
  return (
    <div className="api-credit-banner" role="alert">
      <span className="api-credit-banner-icon" aria-hidden="true">
        ⚠
      </span>
      <span className="api-credit-banner-text">
        <strong>{headline}</strong> {detail}
      </span>
    </div>
  );
}
